import { Context } from "@/interfaces/sdk";
import { SdkExceptLandingPage } from "@/interfaces/sdk-dictionary";
import {
  CategoryPayloads,
  DefinitionPayloads,
  ICategory,
  ICategoryId,
  InstancePayloads,
  InstanceVariables,
  VariblePayloads,
} from "@/interfaces/sdk-flow";
import { JwtActions } from "@/interfaces/sdk-login";
import { getCookie } from "@/utils/cookie";
import fetch from "@mv/flow-shared-utils/functions/fetch";
import { handleErrors } from "@/utils/handle-errors";
const jwt = require("jsonwebtoken") as JwtActions;

function getEnvironmentId() {
  const token = getCookie("access_token");
  const decodedValue = jwt.decode(token);
  const urlAppendix = `?environmentId=${decodedValue.environment.id}&user=${decodedValue.user_name}&logged-user-id=${decodedValue.user_name}`;
  return urlAppendix;
}

export const actions = {
  /* Categories */
  loadCategories() {
    const config = getEnvironmentId();
    return fetch(`flow/api/category${config}`)
      .then(handleErrors)
      .then(({ data }) => data);
  },

  newCategory(_context: Context, { name, description, environmentId }: ICategory) {
    const config = getEnvironmentId();
    return fetch(`flow/api/category/create${config}`, {
      method: "POST",
      body: JSON.stringify({ name, description, environmentId }),
    })
      .then(handleErrors)
      .then((data) => data);
  },

  updateCategory(_context: Context, { id, name, description, environmentId }: ICategory) {
    const config = getEnvironmentId();
    return fetch(`flow/api/category/${id}${config}`, {
      method: "PUT",
      body: JSON.stringify({ name, description, environmentId }),
    })
      .then(handleErrors)
      .then((data) => data);
  },

  deleteCategory(_context: Context, categoryId: ICategoryId) {
    const config = getEnvironmentId();
    return fetch(`flow/api/category/${categoryId.categoryId}${config}`, {
      method: "DELETE",
    })
      .then(handleErrors)
      .then((data) => data);
  },

  changeCategory(_context: Context, { definitionKey, categoryId }: CategoryPayloads) {
    const config = getEnvironmentId();
    return fetch(`flow/api/process-definition/${categoryId}/${definitionKey}${config}`, {
      method: "PUT",
    })
      .then(handleErrors)
      .then((data) => data);
  },

  /* Process Definitions */

  deployDefinition(_context: Context, { xml, definitionId }: DefinitionPayloads) {
    const timeId = new Date().getTime();
    const fileName = `new-process-${timeId}.bpmn`;
    const xmlToBlob: any = new Blob([xml], {
      type: "application/octet-stream;charset=utf-8",
    } as any);
    const xmlTofile = new File([xmlToBlob], fileName);

    const FORM_DATA = new FormData();
    FORM_DATA.append("deployment-name", `new-process-${timeId}`);
    FORM_DATA.append("data.bpmn", xmlTofile);
    FORM_DATA.append("enable-duplicate-filtering", "false");
    FORM_DATA.append("deploy-changed-only", "false");
    FORM_DATA.append("definition-id", definitionId);

    xmlToBlob.name = fileName;
    xmlToBlob.lastModifiedDate = new Date();
    const config = getEnvironmentId();

    return fetch(
      `flow/api/process-definition/create${config}`,
      {
        method: "POST",
        body: FORM_DATA,
      },
      true
    )
      .then(handleErrors)
      .then(async (data) => {
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },

  loadProcessDefinitions(_context: Context, { categoryId }: CategoryPayloads) {
    const config = getEnvironmentId();
    return fetch(`flow/api/process-definition/${categoryId}${config}`, {})
      .then(handleErrors)
      .then((data) => data);
  },

  loadProcessDefinition(_context: Context, { definitionId }: CategoryPayloads) {
    const config = getEnvironmentId();
    return fetch(`flow/rest/process-definition/${definitionId}${config}`)
      .then(handleErrors)
      .then((data) => data);
  },

  deleteProcess(_context: Context, { definitionId, environmentId }: CategoryPayloads) {
    const config = getEnvironmentId();
    return fetch(`flow/api/process-definition/${definitionId}${config}`, {
      method: "DELETE",
      body: JSON.stringify(environmentId),
    })
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },

  loadDefinitionDiagram(_context: Context, { definitionInfo }: DefinitionPayloads) {
    const config = getEnvironmentId();
    const { key, id } = definitionInfo;
    const url = key ? `flow/rest/process-definition/key/${key}/xml${config}` : `flow/rest/process-definition/${id}/xml${config}`;
    return fetch(url)
      .then(handleErrors)
      .then((data) => data);
  },

  /* Process Instance */
  startProcessInstance(_context: Context, { processDefinitionId, businessKey, variables }: InstancePayloads) {
    const config = getEnvironmentId();
    return fetch(`flow/api/process-definition/${processDefinitionId}/submit-form${config}`, {
      method: "POST",
      body: JSON.stringify({ variables, businessKey }),
    });
  },

  loadInstanceVariables(_context: Context, { processInstanceId }: InstancePayloads) {
    const config = getEnvironmentId();
    return fetch(`flow/rest/process-instance/${processInstanceId}/variables${config}`)
      .then(handleErrors)
      .then((data) => data);
  },

  saveVariable(_context: Context, { processInstanceId, variables }: InstancePayloads) {
    const config = getEnvironmentId();
    return fetch(`flow/rest/process-instance/${processInstanceId}/variables${config}`, {
      method: "POST",
      body: JSON.stringify({ modifications: variables }),
    })
      .then(handleErrors)
      .then((data) => data);
  },

  deleteVariable(_context: Context, { processInstanceId, variableName }: VariblePayloads) {
    const config = getEnvironmentId();
    return fetch(`flow/rest/process-instance/${processInstanceId}/variables/${variableName}${config}`, {
      method: "DELETE",
    })
      .then(handleErrors)
      .then((data) => data);
  },

  loadDefinitionIncidents(_context: Context, { definitionId }: { definitionId: string }) {
    const config = getEnvironmentId();
    return fetch(`flow/rest/process-definition/${definitionId}/statistics?incidents=true${config}`)
      .then(handleErrors)
      .then((data) => data);
  },

  loadDefinitionInstances(_context: Context, { definitionId }: { definitionId: string }) {
    const config = getEnvironmentId();
    return fetch(`flow/rest/process-instance?processDefinitionId=${definitionId}${config}`)
      .then(handleErrors)
      .then((data) => data);
  },

  loadDefinitionInstancesByTasks(_context: Context, { taskIds, definitionId }: { taskIds: string[]; definitionId: string }) {
    const config = getEnvironmentId();
    return fetch(`flow/rest/process-instance${config}`, {
      method: "POST",
      body: JSON.stringify({
        activityIdIn: taskIds,
        processDefinitionId: definitionId,
      }),
    })
      .then(handleErrors)
      .then((data) => data);
  },

  loadForms(_context: Context, { productName }: any) {
    const config = getEnvironmentId();
    let url = "";
    const editorUrl = `core/api/documents/published/editor3${config}`;
    const soulUrl = `flow/api/external/task/mvsoul${config}`;
    const pepUrl = `flow/api/external/task/mvpep${config}`;

    switch (productName) {
      case "EDIT":
        url = editorUrl;
        break;
      case "SOUL":
        url = soulUrl;
        break;
      case "PEP":
        url = pepUrl;
        break;
      case "INT":
        break;
      case "API":
        break;
      default:
        break;
    }

    if (url) {
      return fetch(url)
        .then(handleErrors)
        .then((res) => {
          if (res.data) {
            return res.data;
          } else {
            return res;
          }
        });
    }
  },

  loadGroups() {
    const config = getEnvironmentId();
    return fetch(`flow/api/group${config}`)
      .then(handleErrors)
      .then((data) => data);
  },

  saveCheckLandingPageFlow(_state: any, { exceptLandingPage }: { exceptLandingPage: SdkExceptLandingPage }) {
    const config = getEnvironmentId();
    return fetch(`core/api/exceptlandingpage${config}`, {
      method: "POST",
      body: JSON.stringify(exceptLandingPage),
    })
      .then(handleErrors)
      .then((data) => data);
  },
  loadProducts: () => {
    const config = getEnvironmentId();
    return fetch(`core/api/system/integration${config}`)
      .then(handleErrors)
      .then((res) => res)
      .catch((err) => err);
  },
  loadScreens: (_context: Context, { product }: any) => {
    const config = getEnvironmentId();
    return fetch(`core/api/external/screen/origin/${product}${config}`)
      .then(handleErrors)
      .then((res) => res)
      .catch((err) => err);
  },

  checkCreationLimit() {
    const config = getEnvironmentId();
    return fetch(`flow/api/process-definition/is-number-process-limit-reached${config}`)
      .then(handleErrors)
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  },

  downloadProcess(_context: Context, { definitionId }: { definitionId: string }) {
    const config = getEnvironmentId();
    return fetch(`flow/api/process-definition/${definitionId}/data-with-validation${config}`, {
      headers: {
        Accept: "application/xml",
      },
    })
      .then(async (res: any) => {
        if (!res.ok) {
          res = await res.json();
          throw new Array(res.message);
        }
        return res.blob();
      })
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  },

  validateXMLImport(_context: Context, { xml }: { xml: string }) {
    const timeId = new Date().getTime();
    const fileName = `new-process-${timeId}.bpmn`;
    const xmlToBlob: any = new Blob([xml], {
      type: "application/octet-stream;charset=utf-8",
    } as any);
    const xmlTofile = new File([xmlToBlob], fileName);

    const FORM_DATA = new FormData();
    FORM_DATA.append("data.bpmn", xmlTofile);
    FORM_DATA.append("deployment-name", `new-process-${timeId}`);

    xmlToBlob.name = fileName;
    xmlToBlob.lastModifiedDate = new Date();

    const config = getEnvironmentId();
    return fetch(
      `flow/api/process-definition/check-xml${config}`,
      {
        method: "POST",
        body: FORM_DATA,
      },
      true
    )
      .then(handleErrors)
      .then((data) => {
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },

  validateAssigneeInXML(_context: Context, { xml }: { xml: string }) {
    const timeId = new Date().getTime();
    const fileName = `new-process-${timeId}.bpmn`;
    const xmlToBlob: any = new Blob([xml], {
      type: "application/octet-stream;charset=utf-8",
    } as any);
    const xmlTofile = new File([xmlToBlob], fileName);

    const FORM_DATA = new FormData();
    FORM_DATA.append("data.bpmn", xmlTofile);
    const config = getEnvironmentId();

    return fetch(
      `flow/api/process-definition/check-xml/is-exists-unassigned-user-tasks${config}`,
      {
        method: "POST",
        body: FORM_DATA,
      },
      true
    )
      .then(handleErrors)
      .then(({ data }) => {
        return data;
      })
      .catch((err) => {
        throw err;
      });
  },

  findProcesses(_context: Context, { searchText }: { searchText: string }) {
    const config = getEnvironmentId();
    return fetch(`flow/api/process-definition/name${config}&firstResult=00&maxResults=999`, {
      method: "POST",
      body: `%${searchText}%`,
    })
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },

  loadDefinitionHistory(_context: Context, { key }: { key: string }) {
    const config = getEnvironmentId();
    return fetch(`flow/rest/process-definition${config}&key=${key}`)
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },

  copyDefinition(
    _context: Context,
    {
      originalKey,
      newKey,
      categoryId,
      environmentId,
    }: {
      originalKey: string;
      newKey: string;
      categoryId: number;
      environmentId: number;
    }
  ) {
    const config = getEnvironmentId();

    return fetch(`flow/api/process-definition/copy${config}`, {
      method: "POST",
      body: JSON.stringify({
        processKey: originalKey,
        newProcessKey: newKey,
        newEnvironmentId: environmentId,
        category: categoryId,
      }),
    })
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },

  getPaginatedProcessList(_context: Context, { categoryId, page, query }: { categoryId: number; page: number; query: string }) {
    const config = getEnvironmentId();
    let url = `flow/api/process-definition/category/${categoryId}/paginated${config}&pageSize=10&pageNumber=${page}`;
    if (query) {
      url += `&search=${query}`;
    }

    return fetch(url)
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },

  loadPaginatedCategories(_context: Context, { page, query }: { page: number; query: string }) {
    const config = getEnvironmentId();
    let url = `flow/api/category/paginated${config}&pageNumber=${page}&pageSize=10`;
    if (query) {
      url += `&name=${query}`;
    }
    return fetch(url)
      .then(handleErrors)
      .then((data) => data)
      .catch((err) => {
        throw err;
      });
  },
};

export const loadCategories = () => ({ type: "loadCategories" });
export const loadPaginatedCategories = (page: number, query: string) => ({ type: "loadPaginatedCategories", page, query });
export const newCategory = ({ name, description, environmentId }: ICategory) => ({ type: "newCategory", name, description, environmentId });
export const updateCategory = ({ id, name, description, environmentId }: ICategory) => ({
  type: "updateCategory",
  id,
  name,
  description,
  environmentId,
});
export const changeCategory = (categoryId: number, definitionKey: string) => ({
  type: "changeCategory",
  categoryId,
  definitionKey,
});
/** Carrega as definições de processos de uma categoria. */
export const loadProcessDefinitions = (categoryId: number) => ({
  type: "loadProcessDefinitions",
  categoryId,
});
export const deployDefinition = (xml: string, definitionId: number) => ({
  type: "deployDefinition",
  xml,
  definitionId,
});
export const loadProcessDefinition = (definitionId: string) => ({
  type: "loadProcessDefinition",
  definitionId,
});
export const loadForms = (productName: string) => ({
  type: "loadForms",
  productName,
});
export const saveVariable = (processInstanceId: string, variables: InstanceVariables) => ({ type: "saveVariable", processInstanceId, variables });
export const deleteVariable = (processInstanceId: string, variableName: string) => ({ type: "deleteVariable", processInstanceId, variableName });
export const loadDefinitionDiagram = (definitionInfo: DefinitionPayloads["definitionInfo"]) => ({ type: "loadDefinitionDiagram", definitionInfo });
/** Carrega o número de intãncias que estão em cada task da definição */
export const loadDefinitionIncidents = (definitionId: string) => ({
  type: "loadDefinitionIncidents",
  definitionId,
});
export const loadDefinitionInstances = (definitionId: string) => ({
  type: "loadDefinitionInstances",
  definitionId,
});
export const loadDefinitionInstancesByTasks = (taskIds: string[], definitionId: string) => ({
  type: "loadDefinitionInstancesByTasks",
  taskIds,
  definitionId,
});
export const startProcessInstance = (processDefinitionId: string, businessKey: string, variables: number) => ({
  type: "startProcessInstance",
  processDefinitionId,
  businessKey,
  variables,
});
export const loadInstanceVariables = (processInstanceId: string) => ({
  type: "loadInstanceVariables",
  processInstanceId,
});
export const loadGroups = () => ({ type: "loadGroups" });
export const saveCheckLandingPageFlow = (exceptLandingPage: SdkExceptLandingPage) => ({ type: "saveCheckLandingPageFlow", exceptLandingPage });
export const deleteCategory = (categoryId: string) => ({
  type: "deleteCategory",
  categoryId,
});
export const deleteProcess = (definitionId: string, environmentId: string) => ({
  type: "deleteProcess",
  definitionId,
  environmentId,
});
export const checkCreationLimit = () => ({ type: "checkCreationLimit" });
export const downloadProcess = (definitionId: string) => ({
  type: "downloadProcess",
  definitionId,
});
export const validateXMLImport = (xml: string) => ({
  type: "validateXMLImport",
  xml,
});
export const findProcesses = (searchText: string) => ({
  type: "findProcesses",
  searchText,
});
export const validateAssigneeInXML = (xml: string) => ({
  type: "validateAssigneeInXML",
  xml,
});
export const loadDefinitionHistory = (key: string) => ({
  type: "loadDefinitionHistory",
  key,
});
export const copyDefinition = (originalKey: string, newKey: string, categoryId: number, environmentId: number) => ({
  type: "copyDefinition",
  originalKey,
  newKey,
  categoryId,
  environmentId,
});
export const getPaginatedProcessList = (categoryId: number, page: number, query: string) => ({
  type: "getPaginatedProcessList",
  categoryId,
  page,
  query,
});
